import {
  createEntityAdapter,
  createSelector,
  EntityState,
} from '@reduxjs/toolkit';
import { apiSlice } from './api';
import { ResponseBody } from '../types/common';
import { RootState } from './store';
import { SearchParams, SearchRes } from '../types/search';

const searchAdapter = createEntityAdapter({
  selectId: (data: any) => data.project.projectId,
});
const initialState = searchAdapter.getInitialState();

export const searchApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPublicList: builder.query<EntityState<SearchRes>, SearchParams>({
      query: ({
        page = 0,
        search = '',
        size = 24,
        sortBy = 'reg_date',
        sortDir = 'DESC',
      }) => ({
        url: 'search/detail/projects',
        params: { page, search, size, sortBy, sortDir },
      }),
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page && arg.page > 0) {
          currentCache.ids.push(...newItems.ids);
          currentCache.ids = [...new Set(currentCache.ids)];
          currentCache.entities = {
            ...currentCache.entities,
            ...newItems.entities,
          };
        } else {
          return newItems;
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
      transformResponse: (res: ResponseBody) =>
        searchAdapter.setAll(initialState, res.data),
      providesTags: (result, error, arg) =>
        result
          ? [
              'Search',
              ...result.ids.map((id) => ({
                type: 'Search' as const,
                id,
              })),
            ]
          : ['Search'],
    }),
    getPublicProject: builder.query({
      query: (projectId) => `search/detail/projects/${projectId}`,
    }),
    getSearchHistory: builder.query<ResponseBody, void>({
      query: () => ({
        url: 'search/history',
      }),
      providesTags: ['SearchHistory'],
    }),
    delSearchHistory: builder.mutation({
      query: ({ seq }) => ({
        url: `search/history/${seq}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SearchHistory'],
    }),
    delAllSearchHistory: builder.mutation<ResponseBody, void>({
      query: () => ({
        url: `search/history`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SearchHistory'],
    }),
    addSharedLibrary: builder.mutation({
      query: (projectId) => ({
        url: `library/search/register/share/${projectId}`,
        method: 'POST',
      }),
      invalidatesTags: ['Main', 'Share'],
    }),
  }),
});

export const {
  useGetPublicListQuery,
  useGetPublicProjectQuery,
  useGetSearchHistoryQuery,
  useDelSearchHistoryMutation,
  useDelAllSearchHistoryMutation,
  useAddSharedLibraryMutation,
} = searchApiSlice;

export const selectSearchResult = searchApiSlice.endpoints.getPublicList.select(
  { page: 0 }
);

const selectSearchData = createSelector(
  selectSearchResult,
  (searchResult) => searchResult.data
);

export const {
  selectAll: selectAllPublic,
  selectById: selectPublicById,
  selectIds: selectPublicIds,
  selectTotal: selectTotalPublicNum,
} = searchAdapter.getSelectors(
  (state: RootState) => selectSearchData(state) ?? initialState
);
